"use client";

import { useEffect } from "react";
import { useSearchParams } from "next/navigation";
import { Box, Step, StepLabel, Stepper } from "@mui/material";

import { usePasswordReset } from "@/context/passwordReset";
import { useEmailApi } from "@/context/passwordReset/email";
import { useRecaptcha } from "@/context/util/recaptcha";
import { useUi } from "@/context/util/ui";

import LoginButton from "@/components/loginButton";
import ResetButton from "@/components/passwordReset/reset/button";
import ResetForm from "@/components/passwordReset/reset/form";
import SecureButton from "@/components/passwordReset/secure/button";
import SecureForm from "@/components/passwordReset/secure/form";
import ForgotAnswerButton from "@/components/passwordReset/secure/email/button";
import ForgotAnswerForm from "@/components/passwordReset/secure/email/form";
import VerifyButton from "@/components/passwordReset/verify/button";
import VerifyForm from "@/components/passwordReset/verify/form";

export default function PasswordReset() {
  const { alerts, setLoading } = useUi();
  const { executeRecaptcha } = useRecaptcha();
  const { activeStep, setActiveStep } = usePasswordReset();
  const { showForgotAnswer, validateResetToken } = useEmailApi();
  const searchParams = useSearchParams();

  const token = searchParams.get("token");

  useEffect(() => {
    if (activeStep === -1 && executeRecaptcha && token)
      validateResetToken(token ?? "");
    else if (executeRecaptcha && activeStep === -1) {
      setActiveStep(0);
      setLoading(false);
    }
  }, [activeStep, executeRecaptcha, token]);

  function getContent() {
    switch (activeStep) {
      case 0:
        return (
          <>
            <VerifyForm />
            <VerifyButton />
          </>
        );
      case 1:
        if (showForgotAnswer) {
          return (
            <>
              <ForgotAnswerForm />
              <ForgotAnswerButton />
            </>
          );
        }

        return (
          <>
            <SecureForm />
            <SecureButton />
          </>
        );
      case 2:
        return (
          <>
            <ResetForm />
            <ResetButton />
          </>
        );
      case 3:
        return <LoginButton />;
      default:
        return null;
    }
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Stepper activeStep={activeStep}>
        <Step completed={activeStep > 0}>
          <StepLabel>Verify</StepLabel>
        </Step>
        <Step completed={activeStep > 1}>
          <StepLabel>Secure</StepLabel>
        </Step>
        <Step completed={activeStep > 2}>
          <StepLabel>Reset</StepLabel>
        </Step>
      </Stepper>
      <Box
        sx={{
          mb: 2,
          mt: 2,
          width: "100%",
        }}
      >
        {alerts?.get(activeStep)}
        {getContent()}
      </Box>
    </Box>
  );
}
